import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Plus, History, Loader2 } from "lucide-react";
import { useDashboardContext } from "@/components/DashboardLayout";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AverageScoreOverview } from "@/components/AverageScoreOverview";
import type { AnalysisResult } from "@/types/analysis";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useSubscription } from "@/hooks/useSubscription";
import { STRIPE_TIERS } from "@/lib/stripe-config";
import { toast } from "@/hooks/use-toast";

export default function DashboardPage() {
  const { user } = useAuth();
  const { openNewAnalysis } = useDashboardContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { plan, refresh: refreshSub, subscriptionEnd } = useSubscription();
  usePageMeta({ title: "Dashboard — Unranked" });
  const [results, setResults] = useState<AnalysisResult[]>([]);
  const [loading, setLoading] = useState(true);

  // Handle checkout success
  useEffect(() => {
    if (searchParams.get("checkout") !== "success" || !user) return;

    const handleSuccess = async () => {
      // Clear param
      setSearchParams({}, { replace: true });

      // Refresh subscription to get latest plan info
      await refreshSub();
    };

    handleSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user]);

  // Send confirmation email once plan is known after checkout
  useEffect(() => {
    if (!user || plan === "free") return;

    const sent = sessionStorage.getItem("sub_email_sent");
    if (sent) return;

    // Only send if we just came from checkout (within last 30s)
    const checkoutTs = sessionStorage.getItem("checkout_ts");
    if (!checkoutTs || Date.now() - parseInt(checkoutTs) > 30000) return;

    sessionStorage.setItem("sub_email_sent", "1");
    sessionStorage.removeItem("checkout_ts");

    const tierConfig = plan === "starter" ? STRIPE_TIERS.starter : STRIPE_TIERS.growth;
    const renewalFormatted = subscriptionEnd
      ? new Date(subscriptionEnd).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
      : "N/A";

    toast({
      title: `Welcome to ${tierConfig.name}! 🎉`,
      description: "Your subscription is active.",
    });

    supabase.functions.invoke("send-transactional-email", {
      body: {
        templateName: "subscription-confirmation",
        recipientEmail: user.email,
        idempotencyKey: `sub-confirm-${user.id}-${Date.now()}`,
        templateData: {
          planName: tierConfig.name,
          price: `$${tierConfig.price}/mo`,
          analysesIncluded: plan === "growth" ? "Unlimited" : "10 per month",
          renewalDate: renewalFormatted,
        },
      },
    });
  }, [user, plan, subscriptionEnd]);

  // Set timestamp when checkout=success is detected
  useEffect(() => {
    if (searchParams.get("checkout") === "success") {
      sessionStorage.setItem("checkout_ts", Date.now().toString());
    }
  }, []);

  // Handle pending plan from pricing page (unauthenticated user flow)
  useEffect(() => {
    if (!user) return;
    const pendingPlan = localStorage.getItem("pending_plan");
    if (!pendingPlan) return;
    localStorage.removeItem("pending_plan");

    const tier = STRIPE_TIERS[pendingPlan as keyof typeof STRIPE_TIERS];
    if (!tier) return;

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("create-checkout", {
          body: { priceId: tier.price_id },
        });
        if (error) throw error;
        if (data?.url) window.open(data.url, "_blank");
      } catch {
        toast({ title: "Error", description: "Failed to start checkout.", variant: "destructive" });
      }
    })();
  }, [user]);

  // Load analyses
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("analyses")
        .select("results")
        .order("created_at", { ascending: false });
      if (data) {
        setResults(data.map((row) => row.results as unknown as AnalysisResult));
      }
      setLoading(false);
    })();
  }, [user]);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-display">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Welcome back, {user?.email}</p>
        </div>
        <Button onClick={openNewAnalysis} className="gap-2">
          <Plus className="h-4 w-4" />
          New Analysis
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : results.length > 0 ? (
        <AverageScoreOverview results={results} />
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <button
          onClick={openNewAnalysis}
          className="rounded-lg border border-border bg-card p-6 text-left hover:bg-muted/50 transition-colors"
        >
          <Plus className="h-5 w-5 text-primary mb-2" />
          <p className="text-sm font-medium text-foreground">Run a new analysis</p>
          <p className="text-xs text-muted-foreground mt-1">Enter a URL to get a full SEO report.</p>
        </button>
        <button
          onClick={() => navigate("/analyses")}
          className="rounded-lg border border-border bg-card p-6 text-left hover:bg-muted/50 transition-colors"
        >
          <History className="h-5 w-5 text-primary mb-2" />
          <p className="text-sm font-medium text-foreground">Previous analyses</p>
          <p className="text-xs text-muted-foreground mt-1">Revisit your past SEO reports.</p>
        </button>
      </div>
    </div>
  );
}
